import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';

import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';
import { GATE_CTA_CLASS as CTA_CLASS } from '~/modules-rocco/user/component/auth-modal/gate-cta';

/** Matches the enquiry gate's confirmation, which closes on the same count. */
const AUTO_CLOSE_SECONDS = 5;

export interface BookMeetingGateSuccessProps {
    /** Who the meeting was requested with. */
    subjectName: string;
    /** The practice, named as the party who confirms. */
    professionalName: string;
    /** The day and time asked for, already formatted. */
    when: string;
    /**
     * The professional keeps a connected calendar, so the time is booked rather
     * than requested. Only the wording changes: either way the reader has
     * nothing left to do here.
     */
    isBooked: boolean;
    onClose: () => void;
}

/**
 * Success step of the booking gate, laid out as the enquiry gate's is: a light
 * label with a check, the outcome under it, the day and time on a grey surface
 * between them, and the substance at the foot above the action.
 *
 * It closes itself on both tiers. Offering to save the slot to the reader's own
 * calendar was tried and dropped: on the request tier nothing is agreed yet, so
 * the entry would read as a confirmed meeting the professional has not seen —
 * and would quietly be wrong the moment they proposed another time.
 *
 * See BC-5174.
 */
export const BookMeetingGateSuccess = ({
    subjectName,
    professionalName,
    when,
    isBooked,
    onClose,
}: BookMeetingGateSuccessProps) => {
    const [secondsLeft, setSecondsLeft] = useState(AUTO_CLOSE_SECONDS);

    useEffect(() => {
        if (secondsLeft <= 0) {
            onClose();
            return;
        }
        const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [secondsLeft, onClose]);

    return (
        <div className="mx-auto flex min-h-0 w-full max-w-86 flex-1 flex-col">
            <h2 className="mt-9 flex items-center gap-2.5 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]">
                {isBooked ? 'Booked' : 'Request sent'}
                <Check className="size-6 shrink-0" strokeWidth={2} />
            </h2>
            <p className="mt-3 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]">
                {isBooked
                    ? `Your meeting with ${subjectName} is confirmed.`
                    : `${subjectName} will be in touch.`}
            </p>
            {/* The day and time, given the weight it earns as the thing being
                confirmed, rather than trailing the paragraph in grey. */}
            <div className="mt-8 rounded-[1rem] bg-[#f6f6f6] px-6 py-7 text-center">
                <p className="m-0 font-gestura text-[1.75rem] leading-[1.15] text-[#181818]">
                    {when}
                </p>
            </div>
            <div className="flex-1" />
            <p className="mt-8 text-[1.1875rem] leading-snug font-medium tracking-[-0.02em] text-[#181818]">
                {isBooked
                    ? `The invitation is on its way to your inbox, and ${professionalName} has it on their calendar.`
                    : `${professionalName} has your preferred time and will confirm it, or suggest another that suits.`}
            </p>
            <GateStickyFooter className="mt-5" desktopClassName="mb-2">
                <button type="button" onClick={onClose} className={CTA_CLASS}>
                    Close
                </button>
                <p className="mt-4 text-center text-[1.0625rem] tracking-[-0.02em] text-[#44443f]">
                    Closing in {secondsLeft}...
                </p>
            </GateStickyFooter>
        </div>
    );
};
